import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Message } from '../../types';
import { parse, format } from 'date-fns';
import { formatMarkdownToSafeHtml } from '../../../../../utils/html';
import { Check } from 'lucide-react';

interface MessageBubbleProps {
  message: Message;
  onImprove?: (message: Message) => void;
  onTeachResponse?: (message: Message) => void;
  compactSpacing?: boolean;
  containerClassName?: string;
}

const formatTime = (timeString?: string, dateString?: string): string => {
  if (!timeString) return '';

  let messageDate: Date | null = null;
  const fullFormats = ["yyyy-MM-dd'T'HH:mm:ss.SSSxxx", "yyyy-MM-dd'T'HH:mm:ss"];
  for (const f of fullFormats) {
    const parsed = parse(timeString, f, new Date());
    if (!isNaN(parsed.getTime())) {
      messageDate = parsed;
      break;
    }
  }

  if (!messageDate && dateString) {
    const timeFormats = ['hh:mm a', 'HH:mm', 'H:mm'];
    for (const f of timeFormats) {
      const parsedTime = parse(timeString, f, new Date());
      if (!isNaN(parsedTime.getTime())) {
        const dateParts = dateString.split('/');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1;
          const year = parseInt(dateParts[2]);
          messageDate = new Date(year, month, day, parsedTime.getHours(), parsedTime.getMinutes());
        }
        break;
      }
    }
  }

  if (!messageDate) {
    messageDate = new Date(timeString);
  }

  if (!messageDate || isNaN(messageDate.getTime())) return timeString;

  return format(messageDate, 'HH:mm');
};

const MessageBubble = ({ message, onImprove, onTeachResponse, compactSpacing = false, containerClassName = '' }: MessageBubbleProps) => {
  const isBot = Boolean(message.fromBot);
  const isRestaurant = Boolean(message.fromMe && !message.fromBot);
  const isClient = message.sender === 'client' && !isBot && !isRestaurant;
  const hasImage = Boolean(message.imageUrl);
  const hasAudio = Boolean(message.audioUrl);
  const hasContent = Boolean(message.content && message.content.trim());
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (!previewImage) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage]);

  const bubbleClasses = isBot
    ? 'bg-amber-100 text-black border border-amber-200 shadow-sm'
    : isClient
      ? 'bg-white border border-gray-200 text-black'
      : 'bg-[#D9FDD3] text-black';

  return (
    <>
      <div className={`group flex ${isClient ? 'justify-start' : 'justify-end'} ${compactSpacing ? '!mt-1' : ''} ${containerClassName}`}>
        <div
          className={`relative rounded-lg px-4 py-2 ${bubbleClasses} ${
            hasAudio ? 'w-[94%] sm:w-[85%] max-w-none' : 'max-w-[70%]'
          }`}
        >
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              {isBot && (
                <p className="text-xs font-semibold text-amber-700 mb-1">Enviada pela IA</p>
              )}
              {hasImage && (
                <button
                  type="button"
                  onClick={() => setPreviewImage(message.imageUrl!)}
                  className="block max-w-full"
                  aria-label="Abrir imagem em tela cheia"
                >
                  <img
                    src={message.imageUrl || ''}
                    alt={hasContent ? message.content : 'Imagem recebida'}
                    className="max-w-[min(320px,100%)] max-h-[320px] w-auto h-auto rounded-lg border border-black/10 object-cover cursor-zoom-in mb-2"
                  />
                </button>
              )}
              {hasAudio && (
                <div className="mb-2">
                  <audio controls preload="none" className="w-full">
                    <source src={message.audioUrl || ''} type={message.audioType || undefined} />
                  </audio>
                </div>
              )}
              {hasContent && (
                <p
                  className="text-sm whitespace-pre-line break-words text-black"
                  dangerouslySetInnerHTML={{
                    __html: formatMarkdownToSafeHtml(message.content, { style: 'whatsapp' }),
                  }}
                />
              )}
            </div>
            <span className="flex shrink-0 translate-y-1 items-center gap-1 text-xs opacity-60">
              {formatTime(message.timestamp)}
              {(message.sender === 'user' || isRestaurant) && message.deliveryStatus !== 'pending' && (
                <Check size={14} aria-label="Mensagem salva" />
              )}
            </span>
          </div>
        </div>
      </div>
      {previewImage && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm cursor-zoom-out"
            onClick={() => setPreviewImage(null)}
          />
          <div className="relative z-10 max-w-[90vw] max-h-[90vh]">
            <img
              src={previewImage}
              alt="Imagem ampliada"
              className="w-auto max-w-full max-h-[90vh] rounded-2xl shadow-2xl object-contain"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 text-white bg-black/40 rounded-full p-1 focus-visible:outline-none"
              aria-label="Fechar pré-visualização"
            >
              ✕
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MessageBubble;
